import type { ExperimentContext } from '@learnforge/demo-kit';
import {
  safeStorage,
  getShotId,
  createToggleRow,
  createDropdownRow,
  createSliderRow,
} from '@learnforge/demo-kit';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createSvgEl } from '../svg';
import { createFigure, type FigureHandle } from '../figure';
import { createMarks, type MarksHandle } from '../marks';
import { createReadout, type ReadoutHandle } from '../readout';
import { createGrid, type GridHandle, type GridOptions } from '../grid';
import {
  createDissection,
  type DissectionHandle,
  type DissectionOptions,
} from '../geometry/dissection';
import {
  createCompassTools,
  type CompassToolsHandle,
  type CompassToolsOptions,
} from '../geometry/compass-tools';
import type {
  ExperimentDef,
  ExperimentSpec,
  FigureExperimentSpec,
  HarnessContext,
  HarnessController,
  MountedHarnessExperiment,
  PanelRow,
  RenderControls,
  RenderHandle,
} from './types';

const STAGE_VIEWBOX = '0 0 800 460';
const STAGE_WIDTH = 800;
const STAGE_HEIGHT = 460;

/**
 * Adapter that creates the SVG stage + figure controller, calls `spec.setup`
 * once, then drives `spec.render` on every state change. Returns a
 * {@link RenderHandle} so that `mount`'s body is render-agnostic — it just
 * calls `handle.update` / `handle.dispose`.
 *
 * Exported because the migration plan designates `figureRender` as the named,
 * optional figure-path adapter: future SVG experiments can opt in explicitly,
 * and `mount` uses it internally for figure specs. It is NOT exported primarily
 * for test isolation (tests exercise the figure path through `defineExperiment`).
 */
export function figureRender<S, K extends string, Ctx extends ExperimentContext<K>>(
  spec: FigureExperimentSpec<S, K, Ctx>,
  harnessCtx: HarnessContext<K, Ctx>,
  host: HTMLElement,
  controls: RenderControls<S>,
): RenderHandle<S> {
  // ── Stage svg ──
  const svg = createSvgEl('svg', { viewBox: STAGE_VIEWBOX, class: 'geo-stage' });
  host.appendChild(svg);

  // ── The controller the spec uses to build + drive its scene ──
  const controller: HarnessController<S> = {
    svg,
    get advanced() {
      return controls.advanced;
    },
    createFigure(): FigureHandle {
      const figure = createFigure(svg, {
        viewBox: { width: STAGE_WIDTH, height: STAGE_HEIGHT },
      });
      // THE SINGLE drag registration (dispatch by pointId).
      figure.onDrag((pointId: string, at: Vec2) => {
        if (!spec.onDrag) return;
        controls.setState((prev) => spec.onDrag!(prev, pointId, at));
      });
      controls.registerCleanup(() => figure.destroy());
      return figure;
    },
    createMarks(): MarksHandle {
      const marks = createMarks(svg);
      controls.registerCleanup(() => marks.destroy());
      return marks;
    },
    createReadout(): ReadoutHandle {
      const container = document.getElementById('readout') ?? host;
      const readout = createReadout(container);
      controls.registerCleanup(() => readout.destroy());
      return readout;
    },
    createGrid(opts: Omit<GridOptions, 'parent'>): GridHandle {
      const grid = createGrid({ parent: svg, ...opts });
      controls.registerCleanup(() => grid.destroy());
      return grid;
    },
    createDissection(opts: DissectionOptions): DissectionHandle {
      const dissection = createDissection(svg, opts);
      controls.registerCleanup(() => dissection.destroy());
      return dissection;
    },
    createCompassTools(opts: Omit<CompassToolsOptions, 'parent'>): CompassToolsHandle {
      const tools = createCompassTools({ parent: svg, ...opts });
      controls.registerCleanup(() => tools.destroy());
      return tools;
    },
    registerCleanup: (fn) => controls.registerCleanup(fn),
    raf: (cb) => controls.raf(cb),
    cancelRaf: (id) => controls.cancelRaf(id),
    requestRender: () => controls.requestRender(),
    setState: (next) => controls.setState(next),
  };

  // Build the scene's layers ONCE (in z-order).
  spec.setup(harnessCtx, controller);

  return {
    update(state: S): void {
      spec.render(state, harnessCtx);
    },
    dispose(): void {
      // Intentionally empty — and it MUST stay that way.
      // The svg must still be attached when the layer cleanups run LIFO (each
      // layer's destroy() needs the svg in the DOM). The harness removes the svg
      // afterwards via ctx.root.replaceChildren(). Do NOT add any svg/DOM
      // teardown here; doing so would race with or duplicate the harness cleanup.
    },
  };
}

/**
 * Turn an {@link ExperimentSpec} into an {@link ExperimentDef}. The produced
 * `mount` OWNS every piece of scaffolding an experiment used to hand-roll:
 * advanced-flag / preset / shot reading, the panel + action bar, a complete
 * dispose (tracked rAFs cancelled, cleanups run LIFO), and the awaited
 * shot-pose readiness (`ready`).
 *
 * Two paths are supported:
 *
 * **Figure path** (`spec.setup` + `spec.render`): the harness creates the
 * stage `<svg>`, builds the layer controller, and drives layers imperatively.
 * This is the existing path used by all 2d-geometry experiments.
 *
 * **Custom path** (`spec.createRender`): the harness does NOT create a stage
 * `<svg>` or a figure controller. Instead it calls `spec.createRender(ctx,
 * root, controls)` once to get a {@link RenderHandle}, then calls
 * `handle.update(state)` on every state change and `handle.dispose()` on
 * teardown. All harness-owned concerns (advanced, preset, panel, action bar,
 * shot, dispose) still apply.
 *
 * `params` is `[]` — the harness renders the panel itself from `spec.panel`.
 */
export function defineExperiment<
  S,
  K extends string,
  Ctx extends ExperimentContext<K> = ExperimentContext<K>,
>(spec: ExperimentSpec<S, K, Ctx>): ExperimentDef<Ctx, K> {
  return {
    labelKey: spec.labelKey,
    params: [],
    mount(ctx: Ctx): MountedHarnessExperiment {
      // ── 1. Resolve the advanced flag (storage pref OR ?adv=1; default false) ──
      const advancedStorageKey = `${spec.demoId}.${spec.advancedKey}.advanced`;
      const urlParams = new URLSearchParams(window.location.search);
      // A shot is a curated pose captured with chrome hidden; render it with advanced
      // features available so advanced-only poses (e.g. composition mode, extra overlays)
      // appear. Outside shot mode, resolve advanced from the stored pref or ?adv=1.
      let advanced =
        getShotId() !== null ||
        safeStorage.get(advancedStorageKey) === 'true' ||
        urlParams.get('adv') === '1';

      // ── 2. Live harness context (advanced may toggle at runtime) ──
      const harnessCtx = { ...ctx, advanced } as HarnessContext<K, Ctx>;

      // ── Lifecycle bookkeeping ──
      let disposed = false;
      const cleanups: Array<() => void> = [];
      const rafIds = new Set<number>();

      /** Register teardown to run (LIFO) on dispose. */
      function registerCleanup(fn: () => void): void {
        cleanups.push(fn);
      }

      // ── 3. State + the render invoker ──
      let state: S;
      // Undefined until step 6 assigns it. `render()` guards against being called
      // before assignment (e.g. if setup/createRender synchronously calls
      // controls.setState or controls.requestRender). The explicit initial render()
      // call that follows step 6 runs after assignment and picks up any state
      // changes made during setup.
      let renderHandle: RenderHandle<S> | undefined;

      function render(): void {
        if (disposed || !renderHandle) return;
        renderHandle.update(state);
        renderPanel();
        renderActionBar();
      }

      /**
       * Adopt a synchronous handler's returned state (if any) and re-render.
       * A returned updater is applied to the CURRENT `state` — not the `state`
       * closed over by the row's `onChange` — so a row's handler stays correct
       * even when a slider changed `state` since the row was built (slider
       * input skips the panel rebuild; see the slider case below).
       */
      function commit(next: S | ((prev: S) => S) | void): void {
        if (disposed) return;
        if (typeof next === 'function') {
          state = (next as (prev: S) => S)(state);
        } else if (next !== undefined) {
          state = next;
        }
        render();
      }

      // ── 4. RenderControls (shared between both paths) ──
      const controls: RenderControls<S> = {
        get advanced() {
          return advanced;
        },
        setState(next: S | ((prev: S) => S)): void {
          if (disposed) return;
          state = typeof next === 'function' ? (next as (prev: S) => S)(state) : next;
          render();
        },
        requestRender(): void {
          render();
        },
        registerCleanup,
        raf(cb: FrameRequestCallback): number {
          if (disposed) return 0; // 0 is a discard sentinel — a no-op id; passing it to cancelRaf is harmless.
          const id = window.requestAnimationFrame((time) => {
            rafIds.delete(id);
            if (disposed) return;
            cb(time);
          });
          rafIds.add(id);
          return id;
        },
        cancelRaf(id: number): void {
          if (rafIds.delete(id)) {
            window.cancelAnimationFrame(id);
          }
        },
      };

      // ── 5. Initial state (+ ?preset=) ──
      state = spec.initialState(harnessCtx);

      const presetParam = urlParams.get('preset');
      if (presetParam && spec.applyPreset) {
        state = spec.applyPreset(state, presetParam, harnessCtx);
      }

      // ── 6. Build the RenderHandle (figure path OR custom path) ──
      if (spec.createRender) {
        // Custom path: no svg stage, no figure controller.
        renderHandle = spec.createRender(harnessCtx, ctx.root, controls);
      } else {
        // Figure path: svg stage + controller + setup.
        renderHandle = figureRender(spec, harnessCtx, ctx.root, controls);
      }

      // ── 6b. Top-anchor stage svg(s) ──
      // The stage box is usually taller than the scaled content; SVG's default
      // preserveAspectRatio (xMidYMid) centres vertically, so short stages float
      // mid-screen instead of aligning with the top-fixed params panel. Anchor to
      // the top unless the experiment declared its own policy.
      for (const child of Array.from(ctx.root.children)) {
        if (child instanceof SVGSVGElement && !child.hasAttribute('preserveAspectRatio')) {
          child.setAttribute('preserveAspectRatio', 'xMidYMin meet');
        }
      }

      // ── 7a. Panel (rebuilt from spec.panel on every render) ──
      function renderPanel(): void {
        if (!spec.panel) return;
        ctx.panel.replaceChildren();
        const rows = spec.panel(state, harnessCtx);
        for (const row of rows) {
          ctx.panel.appendChild(buildPanelRow(row));
        }
      }

      function buildPanelRow(row: PanelRow<K, S>): HTMLElement {
        switch (row.kind) {
          case 'toggle': {
            const handle = createToggleRow({
              labelKey: row.labelKey,
              i18n: ctx.i18n,
              checked: row.value,
              onChange: (v) => commit(row.onChange(v)),
            });
            return handle.element;
          }
          case 'dropdown': {
            const handle = createDropdownRow({
              id: row.id,
              i18n: ctx.i18n,
              labelKey: row.labelKey,
              options: row.options,
              value: row.value,
              onChange: (v) => commit(row.onChange(v)),
            });
            return handle.element;
          }
          case 'slider': {
            const handle = createSliderRow({
              id: row.id,
              labelKey: row.labelKey,
              i18n: ctx.i18n,
              min: row.min,
              max: row.max,
              step: row.step,
              value: row.value,
              // Only forward `format` when present (exactOptionalPropertyTypes).
              // Wrapped in an arrow so it is not passed as an unbound method ref.
              ...(row.format ? { format: (v: number) => row.format!(v) } : {}),
              // A slider's live input must NOT go through commit()/render(), because
              // render() calls renderPanel() → ctx.panel.replaceChildren(), which would
              // rip the <input type="range"> the user is dragging out of the DOM. A
              // native range drag ends the instant its element is replaced, so the
              // slider could only advance one step per grab (the "slidable tick by
              // tick" bug). Apply the returned state and repaint the SCENE only — a
              // slider's value never gates the panel's structure, and the next
              // non-slider interaction runs a full render() that reconciles the panel.
              onChange: (v) => {
                if (disposed) return;
                const next = row.onChange(v);
                if (typeof next === 'function') {
                  state = (next as (prev: S) => S)(state);
                } else if (next !== undefined) {
                  state = next;
                }
                renderHandle?.update(state);
              },
            });
            return handle.element;
          }
          case 'button': {
            const handle = createToggleRow({
              labelKey: row.labelKey,
              i18n: ctx.i18n,
              checked: false,
              onChange: () => commit(row.onClick()),
            });
            return handle.element;
          }
          case 'advanced-toggle': {
            const handle = createToggleRow({
              labelKey: row.labelKey,
              i18n: ctx.i18n,
              checked: advanced,
              onChange: (v) => {
                advanced = v;
                harnessCtx.advanced = v;
                safeStorage.set(advancedStorageKey, v ? 'true' : 'false');
                render();
              },
            });
            return handle.element;
          }
          case 'custom':
            return row.render();
        }
      }

      // ── 7b. Action bar (plain buttons; reset/randomise always, rest optional) ──
      function renderActionBar(): void {
        const actionSpec =
          typeof spec.actions === 'function' ? spec.actions(state, harnessCtx) : spec.actions;
        if (!actionSpec) return;
        ctx.actionBar.replaceChildren();

        const container = document.createElement('div');
        container.className = 'demo-actionbar';

        const makeButton = (
          labelKey: K,
          onClick: () => void,
          actionId?: string,
        ): HTMLButtonElement => {
          const btn = document.createElement('button');
          btn.className = 'demo-actionbar-btn';
          btn.dataset.labelkey = labelKey;
          if (actionId) btn.dataset.actionId = actionId;
          btn.textContent = ctx.i18n.t(labelKey);
          btn.addEventListener('click', onClick);
          return btn;
        };

        container.appendChild(
          makeButton('action.reset' as K, () => commit(actionSpec.onReset(state))),
        );
        container.appendChild(
          makeButton('action.randomise' as K, () => commit(actionSpec.onRandomise(state))),
        );
        if (actionSpec.viewReset) {
          const viewReset = actionSpec.viewReset;
          container.appendChild(
            makeButton(viewReset.labelKey, () => viewReset.onResetView(), 'view-reset'),
          );
        }

        if (actionSpec.presets && actionSpec.presets.length > 0 && actionSpec.onPreset) {
          // Wrap in an arrow so it is not extracted as an unbound method ref.
          const onPreset = (s: S, id: string): S | void => actionSpec.onPreset!(s, id);
          const presetRow = document.createElement('div');
          presetRow.className = 'demo-actionbar-presets';
          for (const preset of actionSpec.presets) {
            const btn = makeButton(preset.labelKey, () => commit(onPreset(state, preset.id)));
            btn.dataset.presetId = preset.id;
            presetRow.appendChild(btn);
          }
          container.appendChild(presetRow);
        }

        if (actionSpec.undo) {
          const undo = actionSpec.undo;
          container.appendChild(makeButton('action.undo' as K, () => commit(undo.onUndo(state))));
        }
        if (actionSpec.showMe) {
          const showMe = actionSpec.showMe;
          container.appendChild(
            makeButton('action.show-me' as K, () => commit(showMe.onShowMe(state))),
          );
        }
        if (actionSpec.eraser) {
          const eraser = actionSpec.eraser;
          container.appendChild(
            makeButton('action.eraser' as K, () => commit(eraser.onErase(state))),
          );
        }

        ctx.actionBar.appendChild(container);
      }

      // ── Initial render ──
      render();

      // ── 8. Shot path + ready promise ──
      const shotId = getShotId();
      let ready: Promise<void>;
      if (shotId !== null && spec.applyShotState) {
        ready = Promise.resolve(spec.applyShotState(state, shotId)).then((posed) => {
          if (disposed) return;
          state = posed;
          render();
        });
      } else {
        ready = Promise.resolve();
      }

      // ── 9. Complete dispose (idempotent) ──
      const mounted: MountedHarnessExperiment = {
        ready,
        dispose() {
          if (disposed) return;
          disposed = true;

          // Cancel every tracked rAF so no late frame mutates a torn-down scene.
          for (const id of rafIds) {
            window.cancelAnimationFrame(id);
          }
          rafIds.clear();

          // Tear down the render handle (lets bespoke renders clean up their DOM).
          // renderHandle is always assigned by step 6 before dispose() is reachable.
          renderHandle.dispose();

          // Run registered cleanups LIFO (controller-created handles destroy
          // themselves + any spec-registered teardown).
          for (let i = cleanups.length - 1; i >= 0; i--) {
            cleanups[i]!();
          }
          cleanups.length = 0;

          ctx.panel.replaceChildren();
          ctx.actionBar.replaceChildren();
          ctx.root.replaceChildren();
        },
      };

      return mounted;
    },
  };
}
